<script setup>
import {ref, watch} from "vue";
import {COMMENT_PRIORITIES, COMMENT_STATUSES, priorityDisplay, statusDisplay, toDueDate} from "@/scripts/Comment";
import IssueLabels from "@/components/viewer/IssueLabels.vue";

// eslint-disable-next-line no-undef
const props = defineProps({
  comment: {type: Object, default: null}
});

// eslint-disable-next-line no-undef
const emit = defineEmits(['reply', 'update:status', 'update:priority', 'update:dueDate']);

const replyText    = ref(null);
const dueDateMenu  = ref(false);

// Switching threads must not carry a half-written reply over to the next one.
watch(() => props.comment?.id, () => {
  replyText.value = null;
});

const changeStatus = (status) => {
  emit('update:status', {commentId: props.comment.id, status});
};

const changePriority = (priority) => {
  emit('update:priority', {commentId: props.comment.id, priority});
};

const changeDueDate = (date) => {
  emit('update:dueDate', {commentId: props.comment.id, dueDate: date ? toDueDate(date) : null});
  dueDateMenu.value = false;
};

const sendReply = () => {
  if (!replyText.value) {
    return;
  }
  emit('reply', {threadId: props.comment.id, text: replyText.value});
  replyText.value = null;
};
</script>

<template>
  <div v-if="!props.comment" class="comment-thread-placeholder text-medium-emphasis">
    {{ $t('comments.selectPrompt') }}
  </div>

  <div v-else class="comment-thread">
    <div class="comment-thread-header">
      <v-avatar :color="props.comment.getStatusColor()" size="40">
        <span class="text-subtitle-1">{{ props.comment.getAuthorAcronym() }}</span>
      </v-avatar>
      <div class="comment-thread-heading">
        <div class="text-subtitle-2 font-weight-bold">{{ props.comment.author }}</div>
        <div class="text-caption text-medium-emphasis">
          {{ props.comment.getFormattedCommentCreatedDate() }}
        </div>
      </div>
    </div>

    <div class="comment-thread-triage">
      <v-chip class="comment-thread-chip"
              size="small"
              variant="tonal"
              :color="props.comment.getStatusColor()"
              :prepend-icon="props.comment.getStatusIcon()"
              :append-icon="props.comment.canEditStatus ? 'mdi-menu-down' : undefined"
      >
        {{ props.comment.getStatusLabel() }}
        <v-menu v-if="props.comment.canEditStatus" activator="parent">
          <v-list class="comment-thread-menu" density="compact">
            <v-list-subheader>{{ $t('comments.status.title') }}</v-list-subheader>
            <v-list-item v-for="status in COMMENT_STATUSES"
                         :key="status"
                         :active="status === props.comment.status"
                         @click="changeStatus(status)"
            >
              <template v-slot:prepend>
                <v-icon :color="statusDisplay(status).color"
                        :icon="statusDisplay(status).icon"
                        size="small"
                ></v-icon>
              </template>
              <v-list-item-title>{{ $t(`comments.status.${status}`) }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-chip>

      <v-chip class="comment-thread-chip"
              size="small"
              variant="tonal"
              :color="props.comment.getPriorityColor()"
              :prepend-icon="props.comment.getPriorityIcon()"
              :append-icon="props.comment.canEditPriority ? 'mdi-menu-down' : undefined"
      >
        {{ props.comment.getPriorityLabel() }}
        <v-menu v-if="props.comment.canEditPriority" activator="parent">
          <v-list class="comment-thread-menu" density="compact">
            <v-list-subheader>{{ $t('comments.priority.title') }}</v-list-subheader>
            <v-list-item v-for="priority in COMMENT_PRIORITIES"
                         :key="priority"
                         :active="priority === props.comment.priority"
                         @click="changePriority(priority)"
            >
              <template v-slot:prepend>
                <v-icon :color="priorityDisplay(priority).color"
                        :icon="priorityDisplay(priority).icon"
                        size="small"
                ></v-icon>
              </template>
              <v-list-item-title>{{ $t(`comments.priority.${priority}`) }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-chip>

      <v-chip v-if="props.comment.hasDueDate() || props.comment.canEditDueDate"
              class="comment-thread-chip"
              size="small"
              variant="tonal"
              :color="props.comment.getDueDateColor()"
              :prepend-icon="props.comment.getDueDateIcon()"
              :append-icon="props.comment.canEditDueDate ? 'mdi-menu-down' : undefined"
      >
        {{ props.comment.hasDueDate() ? props.comment.getFormattedDueDate() : $t('comments.dueDate.set') }}
        <v-menu v-if="props.comment.canEditDueDate"
                v-model="dueDateMenu"
                activator="parent"
                :close-on-content-click="false"
        >
          <v-card class="comment-thread-duedate-menu">
            <v-date-picker hide-header
                           :model-value="props.comment.hasDueDate() ? new Date(props.comment.dueDate) : undefined"
                           @update:model-value="changeDueDate($event)"
            ></v-date-picker>
            <v-card-actions v-if="props.comment.hasDueDate()">
              <v-btn color="secondary"
                     density="comfortable"
                     prepend-icon="mdi-calendar-remove"
                     @click="changeDueDate(null)"
              >
                {{ $t('comments.dueDate.clear') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-menu>
      </v-chip>

      <issue-labels :comment="props.comment"></issue-labels>
    </div>

    <div v-if="props.comment.title" class="text-subtitle-1 font-weight-bold comment-thread-title">
      {{ props.comment.title }}
    </div>

    <div class="text-body-2 comment-thread-body" v-html="props.comment.getStringifiedText()"></div>

    <v-divider class="my-4"></v-divider>

    <div v-if="props.comment.replies.length > 0" class="comment-thread-replies">
      <div v-for="reply in props.comment.replies" :key="reply.id" class="comment-thread-reply">
        <v-avatar color="secondary" size="32">
          <span class="text-body-2">{{ reply.getAuthorAcronym() }}</span>
        </v-avatar>
        <div class="comment-thread-reply-content">
          <div class="text-caption text-medium-emphasis">
            {{ reply.author }} · {{ reply.getFormattedCommentCreatedDate() }}
          </div>
          <div class="text-body-2" v-html="reply.getStringifiedText()"></div>
        </div>
      </div>
    </div>

    <div v-if="!props.comment.isResolved()" class="comment-thread-reply-box">
      <v-textarea v-model="replyText"
                  clearable
                  variant="outlined"
                  :label="$t('placeholders.replyThread')"
                  rows="3"
                  auto-grow
      ></v-textarea>
      <div class="comment-thread-actions">
        <v-btn color="secondary"
               density="comfortable"
               :disabled="!replyText"
               @click="sendReply()"
        >
          {{ $t('reply') }}
        </v-btn>
      </div>
    </div>

    <div v-else class="text-caption text-medium-emphasis comment-thread-resolved">
      {{ $t('comments.resolvedNoReply') }}
    </div>
  </div>
</template>

<style scoped>
@import '@/assets/styles/components/CommentThreadPanel.css';
</style>
